<template>
  <q-dialog v-model="dialog" persistent :maximized="telaPequena" @hide="$emit('fechar')">
    <q-card class="sugestoes-atualizacao" :class="{ 'sugestoes-atualizacao--maximizado': telaPequena }">
      <q-card-section class="row items-center no-wrap q-pb-sm">
        <div class="text-h6">Sugestões de atualização</div>
        <q-space />
        <q-btn icon="close" flat round dense :disable="aplicando" @click="fechar" />
      </q-card-section>
      <q-separator />

      <q-card-section v-if="carregando" class="flex flex-center q-pa-xl">
        <q-spinner color="primary" size="40px" />
      </q-card-section>

      <q-card-section v-else-if="erroCarga">
        <q-banner class="bg-red-1 text-negative" rounded>{{ erroCarga }}</q-banner>
      </q-card-section>

      <q-card-section v-else-if="!lote || itens.length === 0">
        Não há sugestões pendentes para os seus produtos.
      </q-card-section>

      <template v-else-if="etapa === 'revisar'">
        <q-card-section class="sugestoes-atualizacao__corpo">
          <div class="sugestoes-atualizacao__aviso q-mb-md">
            <p class="q-mb-sm">
              <b>A CAIXA atualizou a tabela de remuneração das lotéricas{{ referenciaTexto }}.</b>
              Abaixo estão as sugestões de atualização para os seus produtos.
            </p>
            <p class="q-mb-sm">
              <b>Revise cada sugestão antes de confirmar</b>: confira o produto, o item da tabela CAIXA e o
              valor. As linhas marcadas foram conferidas pela DouraSoft; as desmarcadas precisam da sua
              avaliação. Você pode alterar o valor de qualquer linha. Nada é alterado até você confirmar.
            </p>
            <p v-if="lote.mensagem" class="q-mb-sm">{{ lote.mensagem }}</p>
            <div class="row items-center q-gutter-md">
              <a v-if="lote.url_tabela" :href="lote.url_tabela" target="_blank" rel="noopener noreferrer">
                Ver a tabela da CAIXA
              </a>
              <span v-if="lote.data_fim" class="text-grey-8">
                Disponível até {{ formatarData(lote.data_fim) }}
              </span>
            </div>
          </div>

          <q-banner v-if="!podeConfirmar" class="bg-orange-1 text-orange-10 q-mb-md" rounded>
            <template v-slot:avatar>
              <q-icon name="lock" color="orange-10" />
            </template>
            <span v-if="responsavelNome">
              Somente o responsável pela empresa (<b>{{ responsavelNome }}</b>) ou o suporte DouraSoft podem
              confirmar estas alterações.
            </span>
            <span v-else>
              Somente o responsável pela empresa ou o suporte DouraSoft podem confirmar estas alterações, e a empresa
              ainda não tem um responsável com acesso ao sistema. Defina-o em Parâmetros → Dados da Empresa, no ícone
              de engrenagem, ou peça ao suporte DouraSoft.
            </span>
          </q-banner>

          <q-markup-table flat bordered dense separator="horizontal" class="sugestoes-atualizacao__tabela">
            <thead>
              <tr>
                <th class="text-left">
                  <q-checkbox
                    dense
                    :model-value="todasMarcadas"
                    :disable="!podeConfirmar || aplicaveis.length === 0"
                    @update:model-value="marcarTodas(todasMarcadas !== true)"
                  />
                </th>
                <th class="text-left">Produto</th>
                <th class="text-left">Item da tabela CAIXA</th>
                <th class="text-right">Hoje</th>
                <th class="text-right">Sugerido</th>
                <th class="text-right">Novo valor</th>
                <th class="text-left">Confiança</th>
                <th class="text-left">Último uso</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in itens"
                :key="item.id"
                :class="{ 'sugestoes-atualizacao__linha--invalida': item.estado_atual === 'invalida' }"
              >
                <td>
                  <q-checkbox
                    v-model="item.selecionado"
                    dense
                    :disable="!podeConfirmar || item.estado_atual === 'invalida'"
                  />
                </td>
                <td>
                  <div>{{ item.produto }}</div>
                  <div v-if="item.estado_atual === 'invalida'" class="text-caption text-negative">
                    Não pode ser aplicada aqui: {{ item.motivo || "o produto mudou depois da sugestão." }}
                  </div>
                  <div v-else-if="item.estado_atual === 'mudou'" class="text-caption text-orange-10">
                    <q-icon name="warning" /> O valor mudou depois da sugestão. Confira o valor de hoje antes de marcar.
                  </div>
                </td>
                <td>
                  <div>{{ item.item_tabela }}</div>
                  <q-chip v-if="item.tipo === 'correcao'" dense square color="blue-1" text-color="blue-10" class="q-ml-none">
                    Correção da regra de crédito
                  </q-chip>
                </td>
                <td class="text-right">
                  <template v-if="divergente(item.atual)">
                    <div class="text-orange-10">Produto {{ formatarValor(item.atual.produto) }}</div>
                    <div class="text-orange-10">Regra {{ formatarValor(item.atual.regra) }}</div>
                  </template>
                  <span v-else>{{ formatarValor(item.atual.produto) }}</span>
                </td>
                <td class="text-right">{{ formatarValor(item.sugerido) }}</td>
                <td class="text-right sugestoes-atualizacao__valor">
                  <q-field
                    v-model="item.novoValor"
                    dense
                    outlined
                    stack-label
                    :disable="!podeConfirmar || item.estado_atual === 'invalida'"
                    :error="item.selecionado && !!erroValor(item)"
                    :error-message="erroValor(item)"
                    :hide-bottom-space="!(item.selecionado && erroValor(item))"
                  >
                    <template v-slot:control="{ id, floatingLabel, modelValue, emitValue }">
                      <money-input
                        v-show="floatingLabel"
                        :id="id"
                        :value="modelValue"
                        class="q-field__native text-right"
                        v-bind="formatoMoeda"
                        @input="emitValue"
                      />
                    </template>
                  </q-field>
                </td>
                <td>
                  <q-chip v-if="item.confianca === 'alta'" dense square color="green-1" text-color="green-10">
                    Conferida
                  </q-chip>
                  <q-chip v-else dense square color="orange-1" text-color="orange-10">Avaliar</q-chip>
                </td>
                <td>{{ formatarData(item.ultimo_uso) }}</td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card-section>

        <q-separator />
        <q-card-actions align="right" class="q-pa-md">
          <span class="text-grey-8 q-mr-auto">{{ selecionadas.length }} de {{ itens.length }} selecionadas</span>
          <q-btn flat label="Fechar" color="grey-8" @click="fechar" />
          <q-btn
            v-if="podeConfirmar"
            unelevated
            color="primary"
            label="Continuar"
            :disable="!podeContinuar"
            @click="irParaConfirmacao"
          />
        </q-card-actions>
      </template>

      <template v-else-if="etapa === 'confirmar'">
        <q-card-section class="sugestoes-atualizacao__corpo">
          <p class="text-subtitle1 q-mb-sm">Você vai alterar {{ selecionadas.length }} produto(s):</p>
          <q-list bordered separator dense class="q-mb-md">
            <q-item v-for="item in selecionadas" :key="item.id">
              <q-item-section>{{ item.produto }}</q-item-section>
              <q-item-section side class="text-black">
                {{ formatarAtual(item.atual) }} → <b>{{ formatarValor(item.novoValor) }}</b>
              </q-item-section>
            </q-item>
          </q-list>

          <p class="q-mb-sm">
            Vale para os fechamentos feitos a partir de agora: o caixa aberto e qualquer caixa antigo que for
            reaberto e fechado de novo passam a usar o valor novo. Caixas fechados que não forem reabertos ficam
            como estão.
          </p>
          <p v-if="naoSelecionadas > 0" class="q-mb-none">
            As {{ naoSelecionadas }} sugestão(ões) não selecionadas continuam aqui para depois.
          </p>
          <q-checkbox
            v-if="naoSelecionadas > 0"
            v-model="recusarNaoSelecionadas"
            dense
            class="q-mb-md"
            label="Não mostrar mais as sugestões que não selecionei"
          />

          <q-input
            v-model="palavraConfirmacao"
            outlined
            dense
            class="q-mt-md sugestoes-atualizacao__palavra"
            :label="`Digite ${PALAVRA_CONFIRMACAO} para aplicar`"
            autocomplete="off"
            :disable="aplicando"
          />

          <q-banner v-if="erroConfirmacao" class="bg-red-1 text-negative q-mt-md" rounded>
            {{ erroConfirmacao }}
          </q-banner>
        </q-card-section>

        <q-separator />
        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Voltar" color="grey-8" :disable="aplicando" @click="etapa = 'revisar'" />
          <q-btn
            unelevated
            color="primary"
            label="Aplicar alterações"
            :loading="aplicando"
            :disable="!palavraConfere"
            @click="aplicar"
          />
        </q-card-actions>
      </template>

      <template v-else-if="etapa === 'resultado'">
        <q-card-section class="sugestoes-atualizacao__corpo">
          <q-banner v-if="resultado.aplicadas.length" class="bg-green-1 text-green-10 q-mb-md" rounded>
            {{ resultado.aplicadas.length }} produto(s) atualizado(s).
          </q-banner>

          <div v-if="resultado.conflitos.length" class="q-mb-md">
            <p class="text-subtitle1 q-mb-sm">Alterados por outra pessoa depois que você abriu as sugestões:</p>
            <q-list bordered separator dense>
              <q-item v-for="r in resultado.conflitos" :key="r.id">
                <q-item-section>
                  <q-item-label>{{ nomeDoItem(r.id) }}</q-item-label>
                  <q-item-label caption>{{ r.mensagem }}</q-item-label>
                </q-item-section>
                <q-item-section v-if="r.atual" side class="text-black">
                  Hoje: {{ formatarAtual(r.atual) }}
                </q-item-section>
              </q-item>
            </q-list>
          </div>

          <div v-if="resultado.naoAplicadas.length">
            <p class="text-subtitle1 q-mb-sm">Não aplicadas:</p>
            <q-list bordered separator dense>
              <q-item v-for="r in resultado.naoAplicadas" :key="r.id">
                <q-item-section>
                  <q-item-label>{{ nomeDoItem(r.id) }}</q-item-label>
                  <q-item-label caption>{{ motivoNaoAplicada(r) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </q-card-section>

        <q-separator />
        <q-card-actions align="right" class="q-pa-md">
          <q-btn
            v-if="resultado.conflitos.length || pendentesRestantes > 0"
            flat
            color="primary"
            label="Revisar as pendentes"
            @click="carregar"
          />
          <q-btn unelevated color="primary" label="Fechar" @click="fechar" />
        </q-card-actions>
      </template>
    </q-card>
  </q-dialog>
</template>

<script>
export const PALAVRA_CONFIRMACAO = "CONFIRMAR";
// Same ceiling as the API (5000 cents): no CAIXA tariff comes close to it.
export const VALOR_MAXIMO = 50;

const centavos = (valor) => Math.round(Number(valor) * 100);

export default {
  name: "modal-sugestoes-atualizacao",
  emits: ["fechar", "aplicado"],
  data() {
    return {
      PALAVRA_CONFIRMACAO,
      dialog: true,
      carregando: false,
      erroCarga: null,
      etapa: "revisar",
      lote: null,
      podeConfirmar: false,
      responsavelNome: null,
      itens: [],
      recusarNaoSelecionadas: false,
      palavraConfirmacao: "",
      aplicando: false,
      erroConfirmacao: null,
      resultado: { aplicadas: [], conflitos: [], naoAplicadas: [] },
      pendentesRestantes: 0,
      formatoMoeda: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2,
        masked: false,
      },
    };
  },
  computed: {
    referenciaTexto() {
      return this.lote && this.lote.referencia ? ` (${this.lote.referencia})` : "";
    },
    telaPequena() {
      return !!(this.$q && this.$q.screen && this.$q.screen.lt.md);
    },
    aplicaveis() {
      return this.itens.filter((item) => item.estado_atual !== "invalida");
    },
    selecionadas() {
      return this.itens.filter((item) => item.selecionado);
    },
    naoSelecionadas() {
      return this.itens.length - this.selecionadas.length;
    },
    todasMarcadas() {
      if (this.selecionadas.length === 0) return false;
      return this.selecionadas.length === this.aplicaveis.length ? true : null;
    },
    podeContinuar() {
      return this.podeConfirmar && this.selecionadas.length > 0 && this.selecionadas.every((item) => !this.erroValor(item));
    },
    palavraConfere() {
      return this.palavraConfirmacao.trim() === PALAVRA_CONFIRMACAO && !this.aplicando;
    },
  },
  created() {
    this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      this.erroCarga = null;
      const res = await this.$store.dispatch("listarTarifasSugestoes");
      this.carregando = false;

      if (!res.ok) {
        this.erroCarga = res.mensagem;
        return;
      }

      const dados = res.dados;
      this.lote = dados.lote || null;
      this.podeConfirmar = !!dados.pode_confirmar;
      this.responsavelNome = dados.responsavel_nome || null;
      // A suggestion whose product changed since it was published starts unchecked: it was
      // reviewed against the old value, so the user has to look at it again. Read-only users
      // (staff, operators) see the same initial selection the responsável will see.
      this.itens = (dados.itens || []).map((item) => ({
        ...item,
        selecionado: item.pre_selecionada && item.estado_atual === "ok",
        novoValor: item.sugerido,
      }));
      this.etapa = "revisar";
      this.palavraConfirmacao = "";
      this.recusarNaoSelecionadas = false;
      this.erroConfirmacao = null;
    },
    marcarTodas(marcar) {
      this.aplicaveis.forEach((item) => {
        item.selecionado = marcar;
      });
    },
    divergente(atual) {
      return atual.produto !== null && atual.regra !== null && centavos(atual.produto) !== centavos(atual.regra);
    },
    erroValor(item) {
      const valor = Number(item.novoValor);
      if (!isFinite(valor) || valor < 0) return "Valor inválido";
      if (valor > VALOR_MAXIMO) return "Valor acima do permitido";
      if (centavos(valor) === 0 && centavos(item.sugerido) !== 0) return "Informe um valor maior que zero";
      return "";
    },
    irParaConfirmacao() {
      this.palavraConfirmacao = "";
      this.erroConfirmacao = null;
      this.etapa = "confirmar";
    },
    async aplicar() {
      if (!this.palavraConfere) return;

      this.aplicando = true;
      this.erroConfirmacao = null;
      const res = await this.$store.dispatch("confirmarTarifasSugestoes", {
        lote_id: this.lote.id,
        confirmacao: this.palavraConfirmacao.trim(),
        recusar_nao_selecionadas: this.recusarNaoSelecionadas,
        // `esperado` is what the user saw: the API refuses the row if it changed since.
        itens: this.selecionadas.map((item) => ({
          id: item.id,
          valor: centavos(item.novoValor) / 100,
          esperado: { produto: item.atual.produto, regra: item.atual.regra },
        })),
      });
      this.aplicando = false;

      if (!res.ok) {
        this.erroConfirmacao = res.mensagem;
        return;
      }

      const resultados = res.resultados;
      this.resultado = {
        aplicadas: resultados.filter((r) => r.resultado === "aplicada"),
        conflitos: resultados.filter((r) => r.resultado === "conflito"),
        naoAplicadas: resultados.filter((r) => r.resultado !== "aplicada" && r.resultado !== "conflito"),
      };
      this.pendentesRestantes = res.pendentes;
      this.etapa = "resultado";
      if (this.resultado.aplicadas.length > 0) {
        this.$emit("aplicado");
      }
    },
    fechar() {
      this.dialog = false;
    },
    nomeDoItem(id) {
      const item = this.itens.find((i) => i.id === id);
      return item ? item.produto : `Sugestão ${id}`;
    },
    motivoNaoAplicada(r) {
      if (r.mensagem) return r.mensagem;
      if (r.resultado === "ja_decidida") {
        return r.decidido_por ? `Já decidida por ${r.decidido_por}.` : "Já tinha sido decidida.";
      }
      return "Sugestão não encontrada.";
    },
    formatarAtual(atual) {
      if (this.divergente(atual)) {
        return `produto ${this.formatarValor(atual.produto)} / regra ${this.formatarValor(atual.regra)}`;
      }
      return this.formatarValor(atual.produto);
    },
    formatarValor(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return `R$ ${Number(valor).toFixed(2).replace(".", ",")}`;
    },
    formatarData(data) {
      const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(data || "");
      return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : "—";
    },
  },
};
</script>

<style lang="stylus">
.sugestoes-atualizacao
  width 1100px
  max-width 95vw

  &--maximizado
    max-width 100vw

  &__corpo
    max-height 65vh
    overflow-y auto

  &--maximizado &__corpo
    max-height none

  &__aviso
    border-left 4px solid #1976d2
    padding-left 12px

  &__valor
    min-width 120px

  &__linha--invalida td
    color #9e9e9e

  &__palavra
    max-width 320px
</style>
